import Links from '@wetransfer/transfer/component/navigation/links';
import {
  FF_REFERRAL_PROGRAM,
  FF_NAV_SIGN_UP_EXPERIMENT,
} from '@wetransfer/transfer/constant/experiments';
import Helper from '@wetransfer/transfer/helper';
import { createProRoute } from '@wetransfer/transfer/helper/routeFactory';
import { getVariant } from '@wetransfer/transfer/modules/experiments';

export const getAccountMenuName = ({
  isCurrentTeamOfMany,
  currentTeam,
  user,
  subdomainName,
}) => {
  if (isCurrentTeamOfMany && currentTeam) {
    return currentTeam.name;
  }

  if (subdomainName) {
    return subdomainName;
  }

  return user.given_name;
};

export const getThumbnailURL = (user) => {
  return user.profile_picture?.thumbnail_url || Helper.getAvatar(user.id);
};

const insertItemAfter = ({ index, items, item }) => {
  const clone = items.slice();
  clone.splice(index, 0, item);

  return clone;
};

export default {
  replaceProfileName: (navigation, contentKey) => {
    const name = contentKey.length > 0 ? contentKey : '';

    return {
      ...navigation,
      account: {
        ...navigation.account,
        name,
      },
    };
  },
  removePricingPageLink: (navigation, itemKey) => {
    const updatedNavigation = { ...navigation };
    updatedNavigation[itemKey].items = updatedNavigation[itemKey].items.filter(
      (item) => {
        return item.url !== '/pricing';
      }
    );

    return updatedNavigation;
  },
  attachReferralLink: (navigation) => {
    const referral =
      getVariant(FF_REFERRAL_PROGRAM) === 'on' ? Links.referralItem : undefined;

    return {
      referral: referral,
      ...navigation,
    };
  },
  attachSignoutLink: (navigation) => {
    return {
      ...navigation,
      account: {
        ...navigation.account,
        // Didn't use #push as it mutates the original object
        items: insertItemAfter({
          index: navigation.account.items.length,
          item: Links.signoutItem,
          items: navigation.account.items,
        }),
      },
    };
  },
  attachSignupLink: (navigation) => {
    const navigationClone = { ...navigation };
    const { account } = navigationClone;
    delete navigationClone.account;

    const navSignUpExperiment = getVariant(FF_NAV_SIGN_UP_EXPERIMENT);

    const signUpDestinationExperiment =
      navSignUpExperiment &&
      getVariant(FF_NAV_SIGN_UP_EXPERIMENT) !== 'variant-a';

    return {
      ...navigationClone,
      signup: {
        name: 'navigation.signup',
        path: signUpDestinationExperiment ? '/sign-up' : createProRoute(),
        queryParams: {
          trk: 'WT202005_signup',
        },
        trackEvent: {
          category: 'navigation_bar',
          action: 'clickNavSignup',
        },
        whenActive: {
          /**
           * See the description of `fragment: 'pro-page-tiers-section'` in the
           * `app/views/page/plus.html.erb` for why is this here.
           * */
          fragment: 'pro-page-tiers-section',
          /**
           * This is explicitly undefined (any falsy value will work), otherwise
           * it would inherit parent's value — the value of `queryParams` of the
           * "parent" of this, `whenActive`, object.
           * This is important here because a user can appear on a /pro page not
           * only by clicking navigation item but also from other links
           * potentially having different query params values. In this case the
           * original query params must be preserved. Otherwise final url for
           * active state will have (different query params &&
           * `shouldBrowserHandle` set to `true`) which will lead to reloading
           * the page.
           *
           * For implementation details see
           * `app/javascript/component/navigation/navigation-item/navigation-item.js`.
           * */
          queryParams: undefined,
          shouldBrowserHandle: true,
        },
      },
      account,
    };
  },
  attachFeaturesMenu: (navigation, user) => {
    // Features menu is placed after Pricing item
    const { pricing, ...rest } = navigation;

    return {
      pricing,
      features: Links.featuresItem(user),
      ...rest,
    };
  },
  attachFeatureSpacesLink: (navigation) => {
    // Projects link is placed after Transfers item
    const { contacts, branding, ...rest } = navigation;

    return {
      ...rest,
      projects: Links.projectsItem,
      contacts,
      branding,
    };
  },
  attachFeaturePricingLink: (navigation) => {
    const navigationClone = { ...navigation };

    const { contacts, branding } = navigationClone;
    delete navigationClone.contacts;
    delete navigationClone.branding;

    return {
      ...navigationClone,
      contacts,
      branding,
      pricing: Links.pricingItem,
    };
  },
  attachUpgradeAccountLink: (navigation, user) => {
    const navigationClone = { ...navigation };

    return {
      ...navigationClone,
      upgradeAccountItem: Links.upgradeAccountItem(user),
    };
  },

  attachFeaturePortalsLink: (navigation) => {
    const navigationClone = { ...navigation };

    return {
      portals: Links.portalsItem,
      ...navigationClone,
    };
  },
  attachChannelName: (navigation, channelName) => {
    return {
      ...navigation,
      account: {
        ...navigation.account,
        name: channelName,
        hasChannelName: true,
      },
    };
  },
  attachFeaturesExperimentLink: (navigation, user) => {
    const navigationClone = { ...navigation };

    return {
      features: Links.featuresItem(navigation, user),
      ...navigationClone,
    };
  },
  attachAccountMenu: (navigation, user) => {
    // Basic account menu structure:
    // ---
    // Account:
    // |-- Transfer
    // |-- Contacts
    // |-- Profile
    // `-- Account

    return {
      ...navigation,
      account: {
        name: 'navigation.signed_in.name',
        notificationDetails: ['onboarding_success', 'already_signed_in'],
        image: getThumbnailURL(user),
        items: [
          {
            ...Links.transfersItem,
            name: 'navigation.user.items.transfers',
          },
          Links.contactsItem,
          Links.brandingItem,
          Links.accountItem,
        ],
        hasChannelName: false,
      },
    };
  },
};
